<template>
  <div class="modern-countdown" ref="root" role="timer" :aria-label="timerAriaLabel">
    <!-- Screen reader friendly time display (throttled to avoid overwhelming assistive tech) -->
    <div class="sr-only" aria-live="polite">
      {{ srLiveText }}
    </div>

    <!-- Progress bar -->
    <div class="modern-countdown__progress" aria-hidden="true">
      <div class="modern-countdown__progress-bar" :style="{ width: progressPercent + '%' }"></div>
    </div>

    <!-- Time display -->
    <div class="modern-countdown__display" :class="{ 'modern-countdown--urgent': isUrgent, 'modern-countdown--pulse': isPulsing }" v-show="show">
      <!-- Days -->
      <template v-if="showDays">
        <span class="modern-countdown__segment">
          <span class="modern-countdown__digits">
            <span class="modern-countdown__digit-slot" v-for="(d, i) in digitChars(days)" :key="'d' + i">
              <transition name="slide" mode="out-in">
                <span class="modern-countdown__digit" :key="d">{{ d }}</span>
              </transition>
            </span>
          </span>
          <span class="modern-countdown__label" v-if="labels.days">{{ labels.days }}</span>
        </span>
        <span class="modern-countdown__separator" aria-hidden="true">:</span>
      </template>

      <!-- Hours -->
      <template v-if="showHours">
        <span class="modern-countdown__segment">
          <span class="modern-countdown__digits">
            <span class="modern-countdown__digit-slot" v-for="(d, i) in digitChars(hours)" :key="'h' + i">
              <transition name="slide" mode="out-in">
                <span class="modern-countdown__digit" :key="d">{{ d }}</span>
              </transition>
            </span>
          </span>
          <span class="modern-countdown__label" v-if="labels.hours">{{ labels.hours }}</span>
        </span>
        <span class="modern-countdown__separator" aria-hidden="true">:</span>
      </template>

      <!-- Minutes -->
      <span class="modern-countdown__segment">
        <span class="modern-countdown__digits">
          <span class="modern-countdown__digit-slot" v-for="(d, i) in digitChars(minutes)" :key="'m' + i">
            <transition name="slide" mode="out-in">
              <span class="modern-countdown__digit" :key="d">{{ d }}</span>
            </transition>
          </span>
        </span>
        <span class="modern-countdown__label" v-if="labels.minutes">{{ labels.minutes }}</span>
      </span>
      <span class="modern-countdown__separator" aria-hidden="true">:</span>

      <!-- Seconds (with optional tenths) -->
      <span class="modern-countdown__segment">
        <span class="modern-countdown__digits">
          <span class="modern-countdown__digit-slot" v-for="(d, i) in digitChars(seconds)" :key="'s' + i">
            <transition name="slide" mode="out-in">
              <span class="modern-countdown__digit" :key="d">{{ d }}</span>
            </transition>
          </span>
        </span>
        <template v-if="showTenths">
          <span class="modern-countdown__tenths-dot">.</span>
          <span class="modern-countdown__digit modern-countdown__digit--tenth">{{ tenths }}</span>
        </template>
        <span class="modern-countdown__label" v-if="labels.seconds">{{ labels.seconds }}</span>
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'modernCountdown',
  props: {
    stop: {
      type: Boolean
    },
    fontFamily: {
      type: String
    },
    labels: {
      type: Object,
      required: false,
      default: function () {
        return {
          days: '',
          hours: '',
          minutes: '',
          seconds: ''
        }
      }
    },
    tenthsMode: {
      type: String,
      default: 'seconds',
      validator: v => ['seconds', 'tenths', 'auto'].includes(v)
    },
    urgentColor: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      now: Date.now(),
      endTime: null,
      totalDuration: 0,
      diff: 0,
      show: false,
      interval: null,
      isStarted: false,
      srLiveText: '',
      lastAnnouncedAt: 0
    }
  },
  computed: {
    diffSeconds() {
      return Math.max(0, this.diff / 1000)
    },
    days() {
      return Math.trunc(this.diffSeconds / 86400)
    },
    hours() {
      return Math.trunc(this.diffSeconds / 3600) % 24
    },
    minutes() {
      return Math.trunc(this.diffSeconds / 60) % 60
    },
    seconds() {
      return Math.trunc(this.diffSeconds) % 60
    },
    tenths() {
      return Math.trunc((this.diffSeconds % 1) * 10)
    },
    showDays() {
      return this.totalDuration >= 86400 && this.days > 0
    },
    showHours() {
      return this.diffSeconds >= 3600
    },
    showTenths() {
      if (this.tenthsMode === 'tenths') return true
      if (this.tenthsMode === 'auto') return this.diffSeconds < 60
      return false
    },
    progressPercent() {
      if (this.totalDuration <= 0) return 100
      return Math.max(0, Math.min(100, (this.diffSeconds / this.totalDuration) * 100))
    },
    isUrgent() {
      return this.diffSeconds > 0 && this.diffSeconds <= 10
    },
    isPulsing() {
      return this.isUrgent
    },
    timerAriaLabel() {
      if (!this.isStarted) return ''
      if (this.diff <= 0) return 'Timer expired'
      return `Game Timer: ${this.screenReaderTimeText}`
    },
    screenReaderTimeText() {
      if (!this.isStarted) return ''
      if (this.diff <= 0) return 'Timer expired'
      let parts = []
      if (this.days > 0) parts.push(`${this.days} day${this.days !== 1 ? 's' : ''}`)
      if (this.hours > 0) parts.push(`${this.hours} hour${this.hours !== 1 ? 's' : ''}`)
      if (this.minutes > 0) parts.push(`${this.minutes} minute${this.minutes !== 1 ? 's' : ''}`)
      parts.push(`${this.seconds} second${this.seconds !== 1 ? 's' : ''}`)
      return parts.join(', ') + ' remaining'
    }
  },
  watch: {
    now() {
      this.diff = this.endTime - Date.now()

      if (this.stop) {
        clearInterval(this.interval)
        this.interval = null
        this.srLiveText = this.screenReaderTimeText
        this.lastAnnouncedAt = Date.now()
        this.$emit('stopped', Math.round(Math.max(0, this.diff / 1000)))
        return
      }

      if (this.diff <= 0) {
        this.diff = 0
        clearInterval(this.interval)
        this.interval = null
        this.srLiveText = this.screenReaderTimeText
        this.lastAnnouncedAt = Date.now()
        this.$emit('end')
        return
      }

      // Throttle SR live-region updates: every 5s normally, every 1s in the
      // final 10 seconds so the urgency is communicated without flooding AT.
      const nowMs = Date.now()
      const secsLeft = this.diff / 1000
      const throttleMs = secsLeft <= 10 ? 1000 : 5000
      if (nowMs - this.lastAnnouncedAt >= throttleMs) {
        this.srLiveText = this.screenReaderTimeText
        this.lastAnnouncedAt = nowMs
      }
    }
  },
  methods: {
    digitChars(value) {
      return String(value).padStart(2, '0').split('')
    },
    start(seconds, textColor, backgroundColor, topBackgroundColor, fontFamily) {
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null
      }

      // Validate seconds input
      const totalSeconds = Number(seconds)
      if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
        this.isStarted = false
        this.totalDuration = 0
        this.diff = 0
        this.endTime = null
        this.show = false
        this.srLiveText = ''
        this.lastAnnouncedAt = 0
        return
      }

      if (!textColor) textColor = '#ffffff'
      if (!backgroundColor) backgroundColor = '#1a1a2e'

      const el = this.$refs.root
      if (el) {
        el.style.setProperty('--text-color', textColor)
        el.style.setProperty('--bg-color', backgroundColor)
        el.style.setProperty('--accent-color', topBackgroundColor || textColor)
        el.style.setProperty('--font-family', fontFamily || "'Avenir', Helvetica, Arial, sans-serif")
        if (this.urgentColor) {
          el.style.setProperty('--urgent-color', this.urgentColor)
        } else {
          el.style.removeProperty('--urgent-color')
        }
      }

      this.totalDuration = totalSeconds
      this.endTime = Date.now() + totalSeconds * 1000
      this.diff = totalSeconds * 1000
      this.show = true
      this.isStarted = true
      this.srLiveText = ''
      this.lastAnnouncedAt = 0

      this.interval = setInterval(() => {
        this.now = Date.now()
      }, 100)
    }
  },
  beforeDestroy() {
    clearInterval(this.interval)
    this.interval = null
  }
}
</script>

<style scoped>
.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.modern-countdown {
  --text-color: #ffffff;
  --bg-color: #1a1a2e;
  --accent-color: #4361ee;
  --urgent-color: #ff4444;
  --font-family: 'Avenir', Helvetica, Arial, sans-serif;

  font-family: var(--font-family);
  text-align: center;
  padding: 24px 16px 12px;
  border-radius: 12px;
  background: var(--bg-color);
  position: relative;
  overflow: hidden;
}

/* Progress bar */
.modern-countdown__progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.1);
}

.modern-countdown__progress-bar {
  height: 100%;
  background: var(--accent-color);
  transition: width 0.3s linear;
  border-radius: 0 2px 2px 0;
}

/* Display */
.modern-countdown__display {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  transition: color 0.5s ease;
}

.modern-countdown__segment {
  display: inline-flex;
  align-items: center;
  position: relative;
}

.modern-countdown__digits {
  display: inline-flex;
  position: relative;
}

.modern-countdown__digit {
  color: var(--text-color);
  font-size: 2.5rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: center;
  line-height: 1.3;
  display: block;
}

.modern-countdown__digit--tenth {
  font-size: 1.8rem;
  width: 0.6em;
}

.modern-countdown__tenths-dot {
  color: var(--text-color);
  font-size: 2.5rem;
  font-weight: 700;
  opacity: 0.6;
  line-height: 1;
}

.modern-countdown__separator {
  color: var(--text-color);
  font-size: 2rem;
  font-weight: 300;
  opacity: 0.4;
  margin: 0 4px;
  line-height: 1;
}

.modern-countdown__label {
  display: block;
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-color);
  opacity: 0.5;
  margin-top: 2px;
  position: absolute;
  bottom: -14px;
  left: 0;
  right: 0;
  text-align: center;
}

.modern-countdown__digit-slot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-size: 2.5rem;
  width: 0.65em;
  height: 1.3em;
  vertical-align: top;
  overflow: hidden;
}

@media (min-width: 1000px) {
  .modern-countdown__digit-slot {
    font-size: 3.2rem;
  }
}

/* Slide transition for digit changes */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.slide-enter {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* Urgent state — final 10 seconds */
.modern-countdown--urgent .modern-countdown__digit,
.modern-countdown--urgent .modern-countdown__separator,
.modern-countdown--urgent .modern-countdown__tenths-dot {
  color: var(--urgent-color);
}

.modern-countdown--urgent .modern-countdown__progress-bar {
  background: var(--urgent-color);
}

/* Pulse animation */
.modern-countdown--pulse {
  animation: countdown-pulse 1s ease-in-out infinite;
}

@keyframes countdown-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.03); }
}

/* Responsive */
@media (min-width: 1000px) {
  .modern-countdown__digit {
    font-size: 3.2rem;
  }
  .modern-countdown__digit--tenth {
    font-size: 2.2rem;
  }
  .modern-countdown__tenths-dot {
    font-size: 3.2rem;
  }
  .modern-countdown__separator {
    font-size: 2.5rem;
  }
}
</style>
